/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { enqueueErrorSnackbar, enqueueInfoSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';

const CHANNEL = Object.freeze({ mode: 'prepend', values: [], excluded: [] });

const initialState = {
  rules: [],
  savedRules: [],
  revision: 0,
  isFetching: false,
  isSaving: false,
  error: null,
  loadError: null,
};

const textKey = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const normalizeChannel = channel => ({
  ...CHANNEL,
  ...(channel || {}),
  values: Array.isArray(channel?.values) ? channel.values : [],
  excluded: Array.isArray(channel?.excluded) ? channel.excluded : [],
});

export const normalizeSuggestionOverrideRule = rule => ({
  _id: rule._id,
  query: String(rule.query || ''),
  language_id: String(rule.language_id || 'default').toLowerCase(),
  enabled: rule.enabled !== false,
  terms: normalizeChannel(rule.terms),
  words: normalizeChannel(rule.words),
});

const errorMessage = error => (
  error?.body?.message
  || error?.response?.data?.message
  || error?.message
  || getLocalizedMessage('notifications.error.savingError', { entity: 'suggestion overrides' })
);

export const loadSuggestionOverrides = createAsyncThunk(
  'suggestionOverrides/load',
  async (shopId, { extra: { serviceRegistry }, dispatch }) => {
    try {
      if (!serviceRegistry.suggestionOverridesApi) {
        throw new Error('Suggestion overrides API is unavailable.');
      }
      return await serviceRegistry.suggestionOverridesApi.load(shopId);
    } catch (error) {
      dispatch(enqueueErrorSnackbar(errorMessage(error), { variant: 'error' }));
      throw error;
    }
  },
);

export const publishSuggestionOverrides = createAsyncThunk(
  'suggestionOverrides/publish',
  async (shopId, {
    extra: { serviceRegistry }, dispatch, getState, rejectWithValue,
  }) => {
    try {
      if (!serviceRegistry.suggestionOverridesApi) {
        throw new Error('Suggestion overrides API is unavailable.');
      }
      const state = getState().suggestionOverrides;
      if (selectHasInvalidSuggestionOverrides(getState())) {
        const message = getLocalizedMessage('suggestionOverrides.validation.fixRules', {});
        dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
        return rejectWithValue(message);
      }
      const rules = state.rules.map((rule) => {
        const normalized = normalizeSuggestionOverrideRule(rule);
        delete normalized._id;
        return normalized;
      });
      const response = await serviceRegistry.suggestionOverridesApi.publish(shopId, rules);
      dispatch(enqueueInfoSnackbar(
        getLocalizedMessage('suggestionOverrides.notifications.published', {}),
        { variant: 'success' },
      ));
      return response;
    } catch (error) {
      dispatch(enqueueErrorSnackbar(errorMessage(error), { variant: 'error' }));
      throw error;
    }
  },
);

const slice = createSlice({
  name: 'suggestionOverrides',
  initialState,
  reducers: {
    addRule: (state) => {
      state.rules.unshift({
        _id: `temp-${Date.now()}-${state.rules.length}`,
        query: '',
        language_id: 'default',
        enabled: true,
        terms: { ...CHANNEL, values: [], excluded: [] },
        words: { ...CHANNEL, values: [], excluded: [] },
      });
    },
    updateRule: (state, { payload: { id, changes } }) => {
      const index = state.rules.findIndex(rule => rule._id === id);
      if (index >= 0) state.rules[index] = { ...state.rules[index], ...changes };
    },
    deleteRule: (state, { payload }) => {
      state.rules = state.rules.filter(rule => rule._id !== payload);
    },
    discardChanges: (state) => {
      state.rules = _.cloneDeep(state.savedRules);
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSuggestionOverrides.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadSuggestionOverrides.fulfilled, (state, { payload }) => {
        const rules = (payload.rules || []).map(normalizeSuggestionOverrideRule);
        state.rules = rules;
        state.savedRules = _.cloneDeep(rules);
        state.revision = Number(payload.revision || 0);
        state.isFetching = false;
      })
      .addCase(loadSuggestionOverrides.rejected, (state, { error }) => {
        state.error = error.message;
        state.loadError = error.message;
        state.isFetching = false;
      })
      .addCase(publishSuggestionOverrides.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(publishSuggestionOverrides.fulfilled, (state, { payload }) => {
        const rules = (payload.rules || []).map(normalizeSuggestionOverrideRule);
        state.rules = rules;
        state.savedRules = _.cloneDeep(rules);
        state.revision = Number(payload.revision || state.revision);
        state.isSaving = false;
      })
      .addCase(publishSuggestionOverrides.rejected, (state, { error, payload }) => {
        state.error = payload || error.message;
        state.isSaving = false;
      });
  },
});

export const { actions } = slice;

export const selectHasChanges = state => !_.isEqual(
  state.suggestionOverrides.rules,
  state.suggestionOverrides.savedRules,
);

const channelInvalid = (channel, valueLimit, maxLength) => {
  if (!['prepend', 'replace'].includes(channel.mode)) return true;
  const values = channel.values.map(textKey);
  const excluded = new Set(channel.excluded.map(textKey));
  return channel.values.length > valueLimit
    || channel.excluded.length > 100
    || channel.values.some(value => !textKey(value) || String(value).trim().length > maxLength)
    || channel.excluded.some(value => !textKey(value) || String(value).trim().length > maxLength)
    || values.some(value => excluded.has(value));
};

export const selectHasInvalidSuggestionOverrides = createSelector(
  state => state.suggestionOverrides.rules,
  (rules) => {
    const identities = rules.map(rule => `${textKey(rule.language_id || 'default')}\0${textKey(rule.query)}`);
    return rules.some((rule, index) => {
      const normalized = normalizeSuggestionOverrideRule(rule);
      const hasEffect = normalized.terms.mode === 'replace'
        || normalized.words.mode === 'replace'
        || normalized.terms.values.length > 0
        || normalized.words.values.length > 0
        || normalized.terms.excluded.length > 0
        || normalized.words.excluded.length > 0;
      return !textKey(normalized.query)
        || normalized.query.trim().length > 200
        || !/^[a-z0-9_-]{1,64}$/.test(normalized.language_id)
        || identities.indexOf(identities[index]) !== index
        || channelInvalid(normalized.terms, 10, 200)
        || channelInvalid(normalized.words, 20, 100)
        || !hasEffect;
    });
  },
);

export default slice.reducer;
